import React, { useEffect, useRef } from 'react';
import { Platform, Alert } from 'react-native';
import { useGetAttachmentByIdQuery } from '../store/api/QueueApi';

/**
 * SecureFileOpener component that handles secure file operations using attachment IDs
 * This component automatically opens files when mounted and calls onComplete when done
 */
const SecureFileOpener = ({ attachmentId, onComplete }) => {
  const hasOpenedRef = useRef(false);

  // Use RTK Query to fetch the attachment by ID
  const {
    data: fileData,
    error,
    isLoading,
  } = useGetAttachmentByIdQuery(attachmentId, {
    skip: !attachmentId,
  });

  useEffect(() => {
    // Only open the file once when we have the data
    if (fileData && !hasOpenedRef.current) {
      hasOpenedRef.current = true;

      try {
        // Convert the file data to a blob for opening
        const binaryData = fileData.data || fileData;

        if (Array.isArray(binaryData)) {
          // Convert array of numbers to Uint8Array
          const uint8Array = new Uint8Array(binaryData);
          const blob = new Blob([uint8Array], {
            type: fileData.type || 'application/octet-stream',
          });

          if (Platform.OS === 'web') {
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');

            // Clean up blob URL after a delay
            setTimeout(() => {
              URL.revokeObjectURL(blobUrl);
            }, 1000);
          } else {
            // For mobile, you might want to use a different approach
            Alert.alert(
              'File Ready',
              'File has been downloaded. Please check your downloads folder or use a file manager to open it.'
            );
          }
        }
      } catch (err) {
        console.error('Error opening secure file:', err);
        Alert.alert('Error', 'Failed to open the file.');
      }
    }
  }, [fileData]);

  useEffect(() => {
    // Call onComplete when the operation is complete (success or error)
    if ((!isLoading && hasOpenedRef.current) || error) {
      // Small delay to ensure the file opening process is complete
      const timer = setTimeout(() => {
        onComplete();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isLoading, error, onComplete]);

  // This component doesn't render anything visible
  return null;
};

export default SecureFileOpener;
